import React from "react";
import { toast } from "sonner";
import { Flag } from "lucide-react";
import { api } from "@/lib/api";

const REASONS = [
  { value: "inapproprie", label: "Contenu inapproprié" },
  { value: "spam", label: "Spam ou publicité" },
  { value: "haine", label: "Haine / discrimination" },
  { value: "faux", label: "Information fausse" },
  { value: "autre", label: "Autre" },
];

/**
 * Report button that opens a small dialog to flag a public content for
 * admin moderation. Anonymous-friendly. Includes a honeypot field.
 */
export default function ReportButton({ targetType, targetId, label = "Signaler", testId }) {
  const [open, setOpen] = React.useState(false);
  const [reason, setReason] = React.useState("inapproprie");
  const [message, setMessage] = React.useState("");
  const [website, setWebsite] = React.useState(""); // honeypot
  const [submitting, setSubmitting] = React.useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/reports", { target_type: targetType, target_id: targetId, reason, message, website });
      toast.success("Signalement envoyé. Merci, l'équipe va vérifier.");
      setOpen(false);
      setMessage("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-cef-pink font-bold underline-offset-2 hover:underline"
        data-testid={testId || `report-${targetType}-${targetId}`}
        aria-label="Signaler ce contenu"
      >
        <Flag className="w-3 h-3" /> {label}
      </button>

      {open && (
        <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <form
            onSubmit={submit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white border-2 border-black rounded-md shadow-brutal w-full max-w-md p-5 grid gap-3 my-8"
            data-testid="report-form"
          >
            <div className="font-display font-black text-lg uppercase tracking-tight">Signaler ce contenu</div>
            <p className="text-xs text-neutral-600">Aide-nous à garder une plateforme saine. Le contenu sera examiné par l'équipe de modération.</p>
            <select className="neo-input" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="report-reason">
              {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            <textarea
              className="neo-input min-h-[80px]"
              placeholder="Détails (optionnel)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={1000}
              data-testid="report-message"
            />
            {/* Honeypot — hidden from humans, filled by bots */}
            <input
              type="text"
              name="website"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
            />
            <div className="flex gap-2">
              <button type="submit" disabled={submitting} className="neo-btn bg-cef-pink text-white flex-1 disabled:opacity-50" data-testid="report-submit">
                {submitting ? "Envoi…" : "Signaler"}
              </button>
              <button type="button" onClick={() => setOpen(false)} className="neo-btn bg-white" data-testid="report-cancel">Annuler</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
